import { useEffect, useState } from "react";
import { BookOpen, UploadCloud, Trash2, Loader2, Save } from "lucide-react";
import AdminHeader from "@/components/AdminHeader";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function fileUrl(id, kind) {
  const base = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("udi_token") || "";
  return `${base}/api/ebooks/${id}/file/${kind}?auth=${encodeURIComponent(token)}`;
}

export default function AdminEbooks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ebooks");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this e-book?")) return;
    try {
      await api.delete(`/admin/ebooks/${id}`);
      toast.success("Deleted");
      setItems((prev) => prev.filter((b) => b.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <AdminHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1
            className="text-2xl sm:text-3xl font-black text-emerald-400 flex items-center gap-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            <BookOpen size={24} /> E-Book Gallery
          </h1>
        </div>

        <UploadForm onUploaded={reload} />

        <section className="bg-zinc-900 rounded-2xl border border-white/10 overflow-hidden">
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <h2 className="font-bold text-white/90">
              All E-Books{" "}
              <span className="text-white/40 text-sm">({items.length})</span>
            </h2>
          </div>
          {loading ? (
            <div className="py-16 text-center text-white/50">Loading…</div>
          ) : items.length === 0 ? (
            <div
              data-testid="admin-ebooks-empty"
              className="py-16 text-center text-white/60"
            >
              No e-books yet. Upload the first one above.
            </div>
          ) : (
            <div
              data-testid="admin-ebooks-grid"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4"
            >
              {items.map((b) => (
                <div
                  key={b.id}
                  data-testid={`admin-ebook-${b.id}`}
                  className="bg-black/40 rounded-xl border border-white/10 overflow-hidden flex flex-col"
                >
                  <div className="aspect-video bg-black/60 flex items-center justify-center overflow-hidden">
                    {b.cover_path ? (
                      <img
                        src={fileUrl(b.id, "cover")}
                        alt={b.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <BookOpen size={30} className="text-white/40" />
                    )}
                  </div>
                  <div className="p-3 flex-1 flex flex-col">
                    <div className="font-bold text-white text-sm line-clamp-1">
                      {b.title}
                    </div>
                    <div className="text-xs text-white/60 line-clamp-2 mt-0.5">
                      {b.description || "—"}
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-[11px]">
                      <span className="text-amber-300 font-semibold">
                        MRP ₹{Number(b.mrp).toLocaleString("en-IN")}
                      </span>
                      <span className="ml-auto text-white/40">
                        {b.pdf_path ? "PDF · " : ""}
                        {b.video_path ? "MP4 · " : ""}
                        {b.cover_path ? "IMG" : ""}
                      </span>
                    </div>
                    <button
                      data-testid={`admin-ebook-delete-${b.id}`}
                      onClick={() => del(b.id)}
                      className="mt-3 inline-flex items-center justify-center gap-1 text-xs text-red-400 hover:text-red-300 border border-red-500/30 rounded-lg py-1.5"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}


function UploadForm({ onUploaded }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [mrp, setMrp] = useState("");
  const [cover, setCover] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [video, setVideo] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Title required");
      return;
    }
    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("description", description.trim());
    fd.append("mrp", Number(mrp || 0));
    if (cover) fd.append("cover", cover);
    if (pdf) fd.append("pdf", pdf);
    if (video) fd.append("video", video);
    setSaving(true);
    try {
      await api.post("/admin/ebooks", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("E-Book uploaded");
      setTitle("");
      setDescription("");
      setMrp("");
      setCover(null);
      setPdf(null);
      setVideo(null);
      onUploaded?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="admin-ebook-upload-form"
      className="bg-zinc-900 rounded-2xl border border-white/10 p-5 space-y-4"
    >
      <div className="flex items-center gap-2 text-emerald-400 font-bold">
        <UploadCloud size={18} /> Upload New E-Book
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <div className="text-xs font-semibold text-white/70 mb-1">Title *</div>
          <input
            data-testid="ebook-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400/60 outline-none"
            required
          />
        </label>
        <label className="block">
          <div className="text-xs font-semibold text-white/70 mb-1">MRP (₹)</div>
          <input
            data-testid="ebook-mrp-input"
            type="number"
            min="0"
            step="1"
            value={mrp}
            onChange={(e) => setMrp(e.target.value)}
            className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400/60 outline-none"
          />
        </label>
      </div>
      <label className="block">
        <div className="text-xs font-semibold text-white/70 mb-1">Description</div>
        <textarea
          data-testid="ebook-desc-input"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400/60 outline-none"
        />
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <FileInput
          testId="ebook-cover-input"
          label="Cover Image (PNG/JPG)"
          accept="image/*"
          file={cover}
          onChange={setCover}
        />
        <FileInput
          testId="ebook-pdf-input"
          label="PDF File"
          accept="application/pdf"
          file={pdf}
          onChange={setPdf}
        />
        <FileInput
          testId="ebook-video-input"
          label="Video (MP4) — optional"
          accept="video/*"
          file={video}
          onChange={setVideo}
        />
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          data-testid="ebook-submit-btn"
          className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm px-5 py-2.5 rounded-lg disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Uploading…" : "Upload E-Book"}
        </button>
      </div>
    </form>
  );
}


function FileInput({ label, accept, file, onChange, testId }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-white/70 mb-1">{label}</div>
      <input
        data-testid={testId}
        type="file"
        accept={accept}
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        className="w-full text-xs text-white/70 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-bold file:bg-emerald-500/20 file:text-emerald-300 hover:file:bg-emerald-500/30 bg-black/40 border border-white/10 rounded-lg"
      />
      {file && (
        <div className="mt-1 text-[11px] text-white/50 truncate">
          {file.name} · {(file.size / 1024).toFixed(0)} KB
        </div>
      )}
    </label>
  );
}
